import { faEye, faPen, faPlus, faTrash, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { useFormikContext } from "formik";
import { cloneDeep } from "lodash";
import React, { useState } from "react";
import { Button } from "react-bootstrap";

import { OIDividendIncomeDetailsForm } from ".";
import { IDividendIncome, IFormITR1, IReviewOSIncomes } from "../../../config/interfaces";
import { useAppSelector } from "../../../redux/Store";
import { ITRFilingSelector } from "../../../redux/slice/ITRFilingSlice";
import FASvgIcon from "../../FASvgIcon";
import PreventEditComponent from "../../PreventEditComponent";
import PriceDisplay from "../../PriceDisplay";
import { ConfirmationModal } from "../../modals";

const OIDividendIncomeDetails: React.FC = () => {
  const { isFilingReadOnly } = useAppSelector(ITRFilingSelector);

  const [showAddFormCanvas, setShowAddFormCanvas] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<IDividendIncome | null>(null);
  const [deleteMsg, setDeleteMsg] = useState<string>("");

  const { values, setFieldValue, setValues } = useFormikContext<IFormITR1>();
  const DividendIncomesDetails = values.IncomeDetails.DividendIncomes;

  const hasNonZeroOrNonEmptyValues = (incomeData: IDividendIncome) => {
    const { DomesticCompanyIncomes, MutualFundsIncomes } = incomeData;

    return [...(DomesticCompanyIncomes[0] || []), ...(MutualFundsIncomes[0] || [])].some(
      (value) => value !== ""
    );
  };

  const handleTotalCalculation = (incomeData: IDividendIncome) => {
    let TotalDividendIncome = 0;
    const DividendIncomes = Object.values(incomeData || {});
    DividendIncomes.forEach((incomes) => {
      if (incomes.length) {
        incomes.forEach((item: any) => {
          if (item.length && item[1]) {
            TotalDividendIncome += Number(item[1]);
          }
        });
      }
    });

    const OtherSourcesIncomes: IReviewOSIncomes = {
      ...values.OthersInc,
      TotalDividendIncome: TotalDividendIncome
    };
    const TotalIncomeOthSrc = Object.values(OtherSourcesIncomes)?.reduce((acc, curr) => {
      return acc + curr;
    }, 0);

    const DATA = {
      OthersInc: OtherSourcesIncomes,
      IncomeOthSrc: TotalIncomeOthSrc
    };

    setValues({ ...cloneDeep(values), ...DATA });
    if (hasNonZeroOrNonEmptyValues(incomeData)) {
      setFieldValue("IncomeDetails.DividendIncomes", incomeData);
    } else {
      setFieldValue("IncomeDetails.DividendIncomes", null);
    }
  };

  return (
    <>
      <section className="mb-5">
        <div className="section-title-wrap">
          <h5>
            Dividend Income<small>Dividend earned from Equities, Mutual Funds</small>
          </h5>
          <PreventEditComponent>
            {DividendIncomesDetails ? (
              <Button
                variant="warning"
                size="sm"
                className="text-white"
                onClick={() => setShowAddFormCanvas(() => true)}
              >
                <FASvgIcon icon={faPen} />
                Edit
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowAddFormCanvas(() => true)}
              >
                <FASvgIcon icon={faPlus} />
                Add
              </Button>
            )}
          </PreventEditComponent>

          {isFilingReadOnly && (
            <Button
              variant="warning"
              size="sm"
              className="text-white"
              onClick={() => setShowAddFormCanvas(() => true)}
            >
              <FASvgIcon icon={faEye} />
              View
            </Button>
          )}
        </div>

        {DividendIncomesDetails ? (
          <dl className="eds-list eds-dl eds-inline-between">
            <RenderListItem
              title="Dividend from equity"
              incomes={DividendIncomesDetails.DomesticCompanyIncomes}
              onDeleteBtnClick={() => {
                setDeleteMsg(() => "Dividend from equity");
                setShowDeleteModal({
                  ...DividendIncomesDetails,
                  DomesticCompanyIncomes: [["", "", ""]]
                });
              }}
            />

            <RenderListItem
              title="Dividend from Mutual Funds"
              incomes={DividendIncomesDetails.MutualFundsIncomes}
              onDeleteBtnClick={() => {
                setDeleteMsg(() => "Dividend from Mutual Funds");
                setShowDeleteModal({
                  ...DividendIncomesDetails,
                  MutualFundsIncomes: [["", "", ""]]
                });
              }}
            />
          </dl>
        ) : null}
      </section>

      <OIDividendIncomeDetailsForm
        showCanvas={showAddFormCanvas}
        handleCloseBtnPress={(incomeData) => {
          if (incomeData) {
            handleTotalCalculation(incomeData);
          }
          setShowAddFormCanvas(() => false);
        }}
        formValues={DividendIncomesDetails}
      />

      <ConfirmationModal
        showModal={!!showDeleteModal}
        iconColorClass="text-danger"
        title="Are you sure?"
        message={<p>{`You want to delete ${deleteMsg}.`}</p>}
        onYesBtnClick={() => {
          if (showDeleteModal) {
            handleTotalCalculation(showDeleteModal);
            setShowDeleteModal(() => null);
          }
        }}
        onNoBtnClick={() => setShowDeleteModal(() => null)}
        icon={faTrashAlt}
      />
    </>
  );
};

export default OIDividendIncomeDetails;

type RenderItemProps = {
  incomes: string[][];
  title: string;
  onDeleteBtnClick: () => void;
};

const RenderListItem: React.FC<RenderItemProps> = (props: RenderItemProps) => {
  const { incomes, title, onDeleteBtnClick } = props;

  let sum = 0;
  let hasValue = false;

  if (incomes?.length) {
    incomes.forEach((item) => {
      if (item[0] || item[1] || item[2]) {
        hasValue = true;
      }

      if (item.length && item[1]) {
        sum += parseInt(item[1]);
      }
    });
  }

  return (
    <>
      {hasValue ? (
        <div className="eds-list--item">
          <dt>{title}</dt>
          <dd>
            <span>
              <PriceDisplay price={sum} />
            </span>
            <PreventEditComponent>
              <Button
                variant="transparent"
                className="icon-only"
                onClick={() => onDeleteBtnClick()}
              >
                <FASvgIcon icon={faTrash} />
              </Button>
            </PreventEditComponent>
          </dd>
        </div>
      ) : null}
    </>
  );
};
